import React from "react";
import "./QuotationDetailChargesTable.scss";
import { CostDetailCharge } from "api/models";
import { formatPriceV2 } from "helpers/commonHelpers";
import { renderAmountPerQuantity } from "helpers/costHelpers";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";

type QuotationDetailChargesTableProps = {
  costDetailsData: any;
  costDetailsLoading: boolean;
  costDataKey: string;
};

function QuotationDetailChargesTable(props: QuotationDetailChargesTableProps) {
  const { costDetailsData, costDetailsLoading, costDataKey } = props;
  const costData: any = costDetailsData?.costs?.[costDataKey];

  const {
    "Origin Charges": originCharges,
    Freight: freightCharges,
    "Destination Charges": destinationCharges,
  } = costData ?? {};

  const costCharges = [...(originCharges ?? []), ...(freightCharges ?? []), ...(destinationCharges ?? [])];

  const getTotalGroupCost = (group: string, costKey: string, currencyKey: string) => {
    if (!costData?.[group]?.length) return 0;

    const hasDifferentCurrencies = costData[group].some(
      (item: any) => item[currencyKey] !== costData[group][0][currencyKey],
    );

    if (hasDifferentCurrencies) {
      return getGroupedByCurrencyCost(costData[group], costKey, currencyKey);
    }

    const totalCost = costData[group].reduce((acc: number, item: any) => acc + item[costKey], 0);
    return `${costData[group][0][currencyKey]} ${formatPriceV2(totalCost.toFixed(2))}`;
  };

  const getGroupedByCurrencyCost = (group: any, costKey: string, currencyKey: string) => {
    const groupedByCurrency = group.reduce((acc: any, item: any) => {
      if (!acc[item[currencyKey]]) {
        acc[item[currencyKey]] = 0;
      }
      acc[item[currencyKey]] += item[costKey];
      return acc;
    }, {});

    return Object.keys(groupedByCurrency).map((currency, i) =>
      i === Object.keys(groupedByCurrency).length - 1
        ? `${currency} ${formatPriceV2(groupedByCurrency[currency])}`
        : `${currency} ${formatPriceV2(groupedByCurrency[currency])} / `,
    );
  };

  if (costDetailsLoading) return <LoadingSkeleton rows={4} />;

  return (
    <div className="quotation-detail-charges-table">
      <table className="charges-table">
        <thead>
          <tr>
            <th>Cost name</th>
            <th>Quantity</th>
            <th>Quoted cost</th>
            <th>Converted cost ({costDetailsData?.total_quoted_amount_currency})</th>
          </tr>
        </thead>
        <tbody>
          {costCharges.length > 0 && (
            <>
              {Object.keys(costData || {}).map(
                (key) =>
                  costData?.[key]?.length > 0 && (
                    <React.Fragment key={key}>
                      <tr className="charge-group">
                        <td>{key}</td>
                        <td />
                        <td>{getTotalGroupCost(key, "amount_origin", "currency_origin")}</td>
                        <td>{getTotalGroupCost(key, "amount_target", "target_currency_code")}</td>
                      </tr>
                      {costData[key].map((item: CostDetailCharge) => (
                        <tr key={item.id}>
                          <td>
                            {item.description}{" "}
                            {costDataKey === "post_booking_in_transit_costs_data" && item.approved_by_shipper && (
                              <span className="font-semibold text-xs text-tertiary">(Approved)</span>
                            )}
                            <div className="text-xs text-secondary-v2 font-regular">{renderAmountPerQuantity(item)}</div>
                          </td>
                          <td>{parseInt(item.quantity.toString())}</td>
                          <td>{`${item.currency_origin} ${formatPriceV2(item.amount_origin)}`}</td>
                          <td>{`${item.target_currency_code} ${formatPriceV2(item.amount_target)}`}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ),
              )}
              <tr>
                <td>Total cost</td>
                <td />
                <td className="font-semibold">
                  {costDetailsData?.lsp_currency} {formatPriceV2(costDetailsData?.total_quoted_amount_lsp)}
                </td>
                <td className="font-semibold">
                  {costDetailsData?.total_quoted_amount_currency} {formatPriceV2(costDetailsData?.total_quoted_amount)}
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default QuotationDetailChargesTable;
